import { useMutation, useQuery } from '@tanstack/react-query'

import { toast } from '@/components/feedback/toast'
import { payslipApi } from '@/features/payslip/api/payslipApi'
import { feriasCalendarioKeys } from '@/features/rh/ferias/hooks/useFeriasCalendario'
import type { PayslipUpload } from '@/features/payslip/types/payslip'
import { armazenamentoApi } from '@/lib/armazenamentoApi'
import { normalizeApiError } from '@/lib/apiEnvelope'
import { queryClient } from '@/lib/queryClient'

import { subResourcesApi } from '../api/subResourcesApi'
import {
  FERIAS_STATUS_LABEL,
  type AvisoFeriasPayload,
  type Ferias,
  type FeriasPayload,
  type FeriasStatus,
  type FeriasUpdatePayload,
} from '../types/ferias'
import { feriasToFormValues, toFeriasUpdatePayload } from '../types/feriasSchema'
import type { AtestadoPayload, DependentePayload } from '../types/subResources'
import { employeeKeys } from './useEmployees'

export function useEmployeeDocumentos(colaboradorId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...employeeKeys.detail(colaboradorId), 'documentos'],
    queryFn: () => subResourcesApi.documentos(colaboradorId),
    enabled,
  })
}

export function useDeleteEmployeeDocumento(colaboradorId: string) {
  return useMutation({
    mutationFn: (documentoId: string) =>
      subResourcesApi.removeDocumento(colaboradorId, documentoId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...employeeKeys.detail(colaboradorId), 'documentos'],
      })
      toast.success('Documento excluído.')
    },
    onError: (error) => toast.error(normalizeApiError(error).detail),
  })
}

export function useEmployeeArquivos(colaboradorId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...employeeKeys.detail(colaboradorId), 'arquivos'],
    queryFn: () => subResourcesApi.arquivos(colaboradorId),
    enabled,
  })
}

export function useDeleteArquivo(colaboradorId: string) {
  return useMutation({
    mutationFn: (idPublico: string) => armazenamentoApi.remove(idPublico),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...employeeKeys.detail(colaboradorId), 'arquivos'],
      })
      toast.success('Arquivo excluído.')
    },
    onError: (error) => toast.error(normalizeApiError(error).detail),
  })
}

/** Prefixo comum da lista e dos arquivos — invalidá-lo atualiza ambos. */
const dependentesKey = (colaboradorId: string) =>
  [...employeeKeys.detail(colaboradorId), 'dependentes'] as const

const invalidateDependentes = (colaboradorId: string) =>
  queryClient.invalidateQueries({ queryKey: dependentesKey(colaboradorId) })

export function useEmployeeDependentes(colaboradorId: string, enabled: boolean) {
  return useQuery({
    queryKey: dependentesKey(colaboradorId),
    queryFn: () => subResourcesApi.dependentes(colaboradorId),
    enabled,
  })
}

export function useCreateDependente(colaboradorId: string) {
  return useMutation({
    mutationFn: (payload: DependentePayload) =>
      subResourcesApi.createDependente(colaboradorId, payload),
    onSuccess: () => void invalidateDependentes(colaboradorId),
  })
}

export function useUpdateDependente(colaboradorId: string) {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: DependentePayload }) =>
      subResourcesApi.updateDependente(colaboradorId, id, payload),
    onSuccess: () => void invalidateDependentes(colaboradorId),
  })
}

export function useDeleteDependente(colaboradorId: string) {
  return useMutation({
    mutationFn: (dependenteId: string) =>
      subResourcesApi.removeDependente(colaboradorId, dependenteId),
    onSuccess: () => {
      void invalidateDependentes(colaboradorId)
      toast.success('Dependente excluído.')
    },
    onError: (error) => toast.error(normalizeApiError(error).detail),
  })
}

export function useDependenteArquivos(
  colaboradorId: string,
  dependenteId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [...dependentesKey(colaboradorId), dependenteId, 'arquivos'],
    queryFn: () => subResourcesApi.dependenteArquivos(colaboradorId, dependenteId),
    enabled,
  })
}

export function useDeleteDependenteArquivo(colaboradorId: string) {
  return useMutation({
    mutationFn: (idPublico: string) => armazenamentoApi.remove(idPublico),
    onSuccess: () => {
      void invalidateDependentes(colaboradorId)
      toast.success('Arquivo excluído.')
    },
    onError: (error) => toast.error(normalizeApiError(error).detail),
  })
}

/** Prefixo comum da lista e dos arquivos — invalidá-lo atualiza ambos. */
const atestadosKey = (colaboradorId: string) =>
  [...employeeKeys.detail(colaboradorId), 'atestados'] as const

export const invalidateAtestados = (colaboradorId: string) =>
  queryClient.invalidateQueries({ queryKey: atestadosKey(colaboradorId) })

export function useEmployeeAtestados(colaboradorId: string, enabled: boolean) {
  return useQuery({
    queryKey: atestadosKey(colaboradorId),
    queryFn: () => subResourcesApi.atestados(colaboradorId),
    enabled,
  })
}

export function useCreateAtestado(colaboradorId: string) {
  return useMutation({
    mutationFn: (payload: AtestadoPayload) =>
      subResourcesApi.createAtestado(colaboradorId, payload),
    onSuccess: () => void invalidateAtestados(colaboradorId),
  })
}

export function useUpdateAtestado(colaboradorId: string) {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AtestadoPayload }) =>
      subResourcesApi.updateAtestado(colaboradorId, id, payload),
    onSuccess: () => void invalidateAtestados(colaboradorId),
  })
}

export function useDeleteAtestado(colaboradorId: string) {
  return useMutation({
    mutationFn: (atestadoId: string) => subResourcesApi.removeAtestado(colaboradorId, atestadoId),
    onSuccess: () => {
      void invalidateAtestados(colaboradorId)
      toast.success('Atestado excluído.')
    },
    onError: (error) => toast.error(normalizeApiError(error).detail),
  })
}

export function useAtestadoArquivos(colaboradorId: string, atestadoId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...atestadosKey(colaboradorId), atestadoId, 'arquivos'],
    queryFn: () => subResourcesApi.atestadoArquivos(colaboradorId, atestadoId),
    enabled,
  })
}

export function useDeleteAtestadoArquivo(colaboradorId: string) {
  return useMutation({
    mutationFn: (idPublico: string) => armazenamentoApi.remove(idPublico),
    onSuccess: () => {
      void invalidateAtestados(colaboradorId)
      toast.success('Arquivo excluído.')
    },
    onError: (error) => toast.error(normalizeApiError(error).detail),
  })
}

/** Prefixo comum da lista e do saldo — invalidá-lo atualiza ambos. */
const feriasKey = (colaboradorId: string) =>
  [...employeeKeys.detail(colaboradorId), 'ferias'] as const

/** Atualiza lista e saldo do colaborador e o calendário geral de férias. */
const invalidateFerias = (colaboradorId: string) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: feriasKey(colaboradorId) }),
    queryClient.invalidateQueries({ queryKey: feriasCalendarioKeys.all }),
  ])

export function useEmployeeFerias(colaboradorId: string, enabled: boolean) {
  return useQuery({
    queryKey: feriasKey(colaboradorId),
    queryFn: () => subResourcesApi.ferias(colaboradorId),
    enabled,
  })
}

export function useFeriasSaldo(colaboradorId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...feriasKey(colaboradorId), 'saldo'],
    queryFn: () => subResourcesApi.feriasSaldo(colaboradorId),
    enabled,
  })
}

export function useCreateFerias(colaboradorId: string) {
  return useMutation({
    mutationFn: (payload: FeriasPayload) => subResourcesApi.createFerias(colaboradorId, payload),
    onSuccess: () => void invalidateFerias(colaboradorId),
  })
}

export function useUpdateFerias(colaboradorId: string) {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: FeriasUpdatePayload }) =>
      subResourcesApi.updateFerias(colaboradorId, id, payload),
    onSuccess: () => void invalidateFerias(colaboradorId),
  })
}

export function useDeleteFerias(colaboradorId: string) {
  return useMutation({
    mutationFn: (feriasId: string) => subResourcesApi.removeFerias(colaboradorId, feriasId),
    onSuccess: () => {
      void invalidateFerias(colaboradorId)
      toast.success('Férias excluídas.')
    },
    onError: (error) => toast.error(normalizeApiError(error).detail),
  })
}

/**
 * Troca só o status. O PUT substitui todos os campos, então reenvia os valores atuais do
 * registro com o status novo.
 */
export function useChangeFeriasStatus(colaboradorId: string) {
  return useMutation({
    mutationFn: ({ ferias, status }: { ferias: Ferias; status: FeriasStatus }) =>
      subResourcesApi.updateFerias(
        colaboradorId,
        ferias.id,
        toFeriasUpdatePayload({ ...feriasToFormValues(ferias), status }),
      ),
    onSuccess: (_, { status }) => {
      void invalidateFerias(colaboradorId)
      toast.success(`Status alterado para ${FERIAS_STATUS_LABEL[status].toLowerCase()}.`)
    },
    onError: (error) => toast.error(normalizeApiError(error).detail),
  })
}

/** Sem toast de erro: o modal do aviso mostra a mensagem da API (409, 400). */
export function useSalvarAvisoFerias(colaboradorId: string) {
  return useMutation({
    mutationFn: ({ feriasId, payload }: { feriasId: string; payload: AvisoFeriasPayload }) =>
      subResourcesApi.salvarAvisoFerias(colaboradorId, feriasId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: feriasKey(colaboradorId) })
      toast.success('Aviso de férias salvo.')
    },
  })
}

export function useRemoverAvisoFerias(colaboradorId: string) {
  return useMutation({
    mutationFn: (feriasId: string) => subResourcesApi.removerAvisoFerias(colaboradorId, feriasId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: feriasKey(colaboradorId) })
      toast.success('Aviso de férias removido.')
    },
  })
}

export function useEmployeePayslips(colaboradorId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...employeeKeys.detail(colaboradorId), 'payslips'],
    queryFn: () => payslipApi.listByEmployee(colaboradorId),
    enabled,
  })
}

export function useCreatePayslip(colaboradorId: string) {
  return useMutation({
    mutationFn: (payload: PayslipUpload) => payslipApi.create(colaboradorId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...employeeKeys.detail(colaboradorId), 'payslips'],
      })
    },
  })
}

export function useDeletePayslip(colaboradorId: string) {
  return useMutation({
    mutationFn: (idPublico: string) => armazenamentoApi.remove(idPublico),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...employeeKeys.detail(colaboradorId), 'payslips'],
      })
      toast.success('Contracheque excluído.')
    },
    onError: (error) => toast.error(normalizeApiError(error).detail),
  })
}
